import {
    ChangeEventHandler, FocusEventHandler, ReactNode, useCallback, useEffect, useRef, useState
} from 'react';
import { IFormControl } from '../../interfaces';
import { ICountry } from '../../../../domain/addresses/interfaces';
import { textFilter } from '../../../../domain/jplist/actions/filter-provider';
import PhoneFieldAutocomplete from './phone-field-autocomplete';
import { getInitialSelectedCountryId } from './countries-autocomplete-provider';
import { USA_ID } from '../../../../domain/addresses/addresses-provider';

interface IFormPhoneField {
    id: string,
    label: string,
    focus?: boolean,

    phoneControl: IFormControl,
    phoneCountryIdControl: IFormControl,
    countries: ICountry[],

    // event handlers
    onBlur?: FocusEventHandler,
    onPhoneChange?: ChangeEventHandler,
    //onKeyUp?: KeyboardEventHandler,
    onCountryIdChange?: Function,

    // html content
    comment?: ReactNode,

    // styles
    rootClasses?: string,
    textBoxClasses?: string,

    qa?: string,
}

/**
 * Phone field.
 */
const FormPhoneField = (props: IFormPhoneField) => {

    const [isOpened, setIsOpened] = useState(false);
    const [filteredCountries, setFilteredCountries] = useState<ICountry[]>([...props.countries]);
    const [selectedCountryID, setSelectedCountryID] = useState(getInitialSelectedCountryId(filteredCountries));

    // can be used for focus on page load
    const controlRef = useRef<HTMLInputElement>(null);

    const selectedCountry = props.countries.find(country => country.countryID.toString() === props.phoneCountryIdControl.value.toString());
    const countryPhoneCode = selectedCountry ? selectedCountry.phoneCode : USA_ID;
    const countryIconClass = selectedCountry ? selectedCountry.country2Digit.toLowerCase() : 'us';

    /**
     * handle possible focus on page load
     */
    useEffect(() => {
        if(!props.focus || !controlRef || !controlRef.current) return;
        controlRef.current.focus();
    }, [props.focus]);

    /**
     * close the dropdown
     */
    const close = useCallback(() => {
        setIsOpened(false);
        setFilteredCountries([...props.countries]);
    }, [ props.countries ]);

    /**
     * open the drop-down
     */
    const open = () => {
        setIsOpened(true);

        if(controlRef && controlRef.current){
            controlRef.current.focus();
        }
    };

    /**
     * toggle dropdown menu
     */
    const toggle = () => {
        if(isOpened){
            close();
        }
        else{
            open();
        }
    };

    /**
     * filter countries drop-down
     */
    const filter = (term: string) => {
        const filtered = textFilter([...props.countries], term, 'countryDisplayName', 'startsWith') as ICountry[];
        setFilteredCountries(filtered);
        setSelectedCountryID(getInitialSelectedCountryId(filtered));
    };

    /**
     * handle outside click / keydown
     */
    useEffect(() => {

        const handleOutsideClick = () => {
            close();
        };

        const handleKeydown = (evt: KeyboardEvent) => {
            if(evt.key === 'Escape') {
                close();
            }
        };

        document.addEventListener('click', handleOutsideClick);
        document.addEventListener('keydown', handleKeydown);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
            document.removeEventListener('keydown', handleKeydown);
        };
    }, [ close ]);

    /**
     * when user select a country from the phone drop-down
     */
    const selectCountry = (countryID: number) => {
        if(!Number.isInteger(countryID) || countryID <= 0 || !props.onCountryIdChange) return;

        // validate that country with the provided id exists in our list
        const country = props.countries.find(country => country.countryID === countryID);
        if(!country) return;

        // close the drop-down
        close();

        // update selected country index used in autocomplete up / down keys
        setSelectedCountryID(countryID);

        // call the callback
        props.onCountryIdChange(countryID);
    };

    return (
        <div className={ props.rootClasses ? props.rootClasses : 'mb-6'} data-qa={ props.qa }>
            <div className={`w-form-field w-form-phone-field ${!props.phoneControl.isValid ? 'w-form-error' : ''} `}>

                <div className={`w-form-phone__country absolute bg-white`}>
                    <button
                        type="button"
                        className="flex items-center"
                        onClick={ evt => {
                            evt.stopPropagation();
                            toggle();
                        }}>
                        <span className={`w-form-phone__country-flag inline-block flag-icon flag-icon__${ countryIconClass }`}></span>
                        <span className="w-form-phone__country-code ml-2">+{ countryPhoneCode }</span>
                    </button>

                    {
                        isOpened &&
                        <PhoneFieldAutocomplete
                            filteredCountries={ filteredCountries }
                            selectCountry={ selectCountry }
                            filter={ filter }
                            selectedCountryID={ selectedCountryID }
                            setSelectedCountryID={ setSelectedCountryID }
                        />
                    }
                </div>

                <input
                    id={ props.id }
                    className={`w-form-tb ${ props.textBoxClasses || '' } ${ props.phoneControl.value.length > 0 ? 'not-empty': '' }`}
                    type="tel"
                    aria-label="phone"
                    autoComplete="off"
                    minLength={ 5 }
                    maxLength={ 12 }
                    placeholder=""
                    value={ props.phoneControl.value }
                    onChange={ props.onPhoneChange }
/*
                    onKeyUp={ props.onKeyUp }
*/
                    onBlur={ props.onBlur }
                    ref={ controlRef }
                />

                <label htmlFor={ props.id } className="w-form-label">{ props.label }</label>
            </div>

            { props.phoneControl.isValid && props.comment }

            {
                !props.phoneControl.isValid &&
                <div className="text-red text-sm">{ props.phoneControl.error }</div>
            }
        </div>
    )
};

export default FormPhoneField;